import debounce from '../../common/debounce.js'
import param from '../../common/param.js'
import {
    saveFilters,
    loadFilters,
} from './helpers.js'

/**
 * Builds a QueryBuilder-compatible query object from searchArgs, using the same
 * field-type tags mixinsFactory's local mode uses - but only the subset
 * QueryBuilder's REST operator grammar actually supports: String, Number, Date,
 * 'stringMatches'. 'daterange' and custom predicate functions are local-only
 * concepts (a two-bound range needs a repeated array key QueryBuilder's
 * $allowed rules don't map cleanly onto here, and a function can't run against
 * a row it doesn't have) - mixinsFactory throws if either is used with a `url`.
 */
function buildFilterParams(fieldTypes, searchArgs) {
    const params = {}

    for (const key of Object.keys(fieldTypes)) {
        const type = fieldTypes[key]
        const value = searchArgs[key]

        if (type === String || type === 'stringMatches') {
            if (typeof value === 'string' && value.trim().length > 0) {
                params[key] = `~${ value.trim() }`
            }
        } else if (type === Number) {
            if (value !== null && value !== '' && !isNaN(value)) {
                params[key] = value
            }
        } else if (type === Date) {
            if (typeof value === 'string' && value.trim().length > 0) {
                params[key] = value.trim()
            }
        }
    }

    return params
}

function buildDefaultSearchArgs(fieldTypes) {
    const defaults = {}

    for (const key of Object.keys(fieldTypes)) {
        const type = fieldTypes[key]

        defaults[key] = type === Number ? null : '' // String, Date, 'stringMatches'
    }

    return defaults
}

function toBase64(str) {
    try {
        return btoa(unescape(encodeURIComponent(str)))
    } catch {
        return str
    }
}

function sortedSearchArgs(searchArgs) {
    const sorted = {}

    for (const key of Object.keys(searchArgs).sort()) {
        sorted[key] = searchArgs[key]
    }

    return sorted
}

// Deterministic cache key for a given filters+sort+page+pageSize combination -
// same inputs always produce the same key, regardless of property order.
function buildCacheKey({ searchArgs, currentSort, currentSortDir, page, perPageActive }) {
    return toBase64(JSON.stringify({
        searchArgs: sortedSearchArgs(searchArgs),
        currentSort,
        currentSortDir,
        page,
        perPageActive,
    }))
}

// Total row count only depends on filters, not sort/page/pageSize - a
// narrower key than buildCacheKey so the count isn't re-fetched on every
// page click or sort change, only when the filters actually change.
function buildFiltersCacheKey(searchArgs) {
    return toBase64(JSON.stringify(sortedSearchArgs(searchArgs)))
}

const RESERVED_SETTINGS_KEYS = ['url', 'cacheSize', 'debounceMs', 'primaryKey']

/**
 * Remote-backed variant of mixinsFactory's field-map mixin. Instead of
 * filtering/sorting an already-loaded `rows` array in the browser, it fetches
 * one page at a time from `settings.url` whenever searchArgs, currentSort,
 * currentSortDir, or the page/perPage requested by <wn-table-pages> change.
 *
 * Each fetched page is cached in-memory, keyed by a base64 digest of
 * { searchArgs, currentSort, currentSortDir, page, perPageActive } - flipping
 * back to a filter/sort/page combination already seen this session reuses the
 * cached rows instead of hitting the endpoint again. The cache is an LRU of at
 * most `settings.cacheSize` entries (default 20); the oldest entry is evicted
 * once that's exceeded. Pass `cacheSize: 0` to disable caching entirely.
 *
 * Filter changes are debounced by `settings.debounceMs` (default 300) before
 * triggering a refetch - sort/page changes always refetch immediately.
 *
 * `settings.primaryKey` (required) is the $allowed field name to request via
 * QueryBuilder's `?count=` operator for a total row count. Most endpoints
 * don't return a total alongside paginated rows in one call (QueryBuilder's
 * `?count=` replaces the row query rather than running alongside it), so if a
 * response doesn't include `totalCount` itself, a second, filters-only
 * request is made automatically with `?count=<primaryKey>&select=<primaryKey>`
 * instead. The `select=` is required, not cosmetic: without it, QueryBuilder's
 * filter() selects every $allowed field by default, which can trigger unrelated
 * joins that register their own GROUP BY (e.g. a 'tags' or 'images' field
 * grouping by the primary key) - useCount() does not strip GROUP BY, so an
 * untargeted count query silently turns into a per-group count instead of one
 * total (confirmed against QueryBuilder/Select source after a real product
 * endpoint returned `count: 1` instead of the true total). That result is
 * cached separately from the rows cache (keyed by filters only, not
 * sort/page/pageSize) so it isn't re-fetched on every page click or sort
 * change, only when the filters actually change. If an endpoint is later
 * updated to return `totalCount` directly, this second request stops
 * happening automatically - nothing else needs to change on the frontend.
 *
 * Requires a ref named "tablePages" on the <wn-table-pages> element and a
 * `:totalCount="totalCount" @pageChange="onPageChange"` binding on it - see
 * references/tables-and-pagination.md in the vue-common-components skill for
 * the full usage pattern and what the endpoint itself needs to return.
 */
export function createRemoteMixin(settings) {
    const { url, cacheSize, debounceMs, primaryKey } = settings

    if (typeof primaryKey !== 'string' || primaryKey.length === 0) {
        throw new Error(
            'mixinsFactory: a remote (url) mixin requires a "primaryKey" setting - '
            + 'the $allowed field name to request via QueryBuilder\'s ?count= operator '
            + 'when the endpoint\'s response does not already include a totalCount.'
        )
    }

    const fieldTypes = { ...settings }
    RESERVED_SETTINGS_KEYS.forEach((key) => delete fieldTypes[key])

    for (const key of Object.keys(fieldTypes)) {
        const type = fieldTypes[key]

        if (type !== String && type !== Number && type !== Date && type !== 'stringMatches') {
            throw new Error(
                `mixinsFactory: field "${ key }" uses an unsupported type for a remote (url) mixin. `
                + `Only String, Number, Date, and 'stringMatches' are supported - `
                + `'daterange' and custom functions don't map onto QueryBuilder's REST operators.`
            )
        }
    }

    const maxCacheEntries = typeof cacheSize === 'number' && cacheSize >= 0 ? cacheSize : 20
    const searchDebounceMs = typeof debounceMs === 'number' && debounceMs >= 0 ? debounceMs : 300
    const defaultSearchArgs = buildDefaultSearchArgs(fieldTypes)

    return {
        data: () => ({
            namespace: null,
            rows: [],
            totalCount: 0,
            page: 1,
            perPageActive: 10,
            searchArgs: defaultSearchArgs,
            currentSortDir: 'asc',
            currentSort: '',
            loading: false,
            fetchError: null,
        }),
        computed: {
            // Already filtered/sorted/paginated server-side - kept so templates
            // written against the local mixin's API still work unchanged.
            filteredRows() {
                return this.rows
            },
            sortedRows() {
                return this.rows
            },
        },
        watch: {
            searchArgs: {
                deep: true,
                immediate: true,
                handler(val, oldVal) {
                    if (!oldVal) return // first run, nothing to persist/refetch yet

                    if (this.namespace) {
                        saveFilters(this.namespace, val)
                    }

                    this._debouncedRemoteRefresh()
                },
            },
            currentSort() {
                this.refreshFromFirstPage()
            },
            currentSortDir() {
                this.refreshFromFirstPage()
            },
        },
        methods: {
            setSort(key, forceSort = null) {
                if (forceSort) {
                    this.currentSortDir = forceSort
                } else if (key === this.currentSort) {
                    this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc'
                }
                this.currentSort = key
            },
            onSearchChange(evt, name) {
                this.searchArgs[name] = evt.target.value
            },
            // <wn-table-pages ref="tablePages"> owns page/perPage UI state in
            // server mode; calling its changePage() re-emits pageChange, which
            // onPageChange below turns back into a fetch for page 1.
            refreshFromFirstPage() {
                const table = this.$refs.tablePages

                if (table) {
                    table.changePage(1)
                    return
                }

                this.page = 1
                this.fetchRows()
            },
            onPageChange({ page, perPage }) {
                this.page = page
                this.perPageActive = perPage
                this.fetchRows()
            },
            // Drop every cached page and every cached total count - call this
            // after an action that changes the result set server-side
            // independently of filters/sort/page (e.g. creating, editing, or
            // deleting a row elsewhere), since a cache hit would otherwise
            // keep showing the stale response.
            clearCache() {
                this._cache.clear()
                this._countCache.clear()
            },
            // Only called when a normal fetch's response didn't already
            // include totalCount - fires a second, count-only request with
            // the same filters (ignoring sort/page/pageSize, which don't
            // affect how many rows match).
            async fetchTotalCount() {
                const filtersKey = buildFiltersCacheKey(this.searchArgs)

                if (this._countCache.has(filtersKey)) {
                    return this._countCache.get(filtersKey)
                }

                const query = {
                    ...buildFilterParams(fieldTypes, this.searchArgs),
                    // Explicit select= keeps this to just the primary key column -
                    // without it, QueryBuilder's filter() selects every $allowed
                    // field by default, which can pull in unrelated joins that
                    // register their own GROUP BY (e.g. a 'tags'/'images' field
                    // grouping by productId), corrupting ?count= into a per-group
                    // count instead of one total (useCount() doesn't touch GROUP BY).
                    select: primaryKey,
                    count: primaryKey,
                }

                const { data } = await this.$_http({
                    method: 'GET',
                    url: `${ url }?${ param(query) }`,
                    withCredentials: true,
                })

                const count = Number.isFinite(data.count) ? data.count : 0

                if (maxCacheEntries > 0) {
                    this._countCache.set(filtersKey, count)

                    if (this._countCache.size > maxCacheEntries) {
                        this._countCache.delete(this._countCache.keys().next().value)
                    }
                }

                return count
            },
            async fetchRows(force = false) {
                const requestId = ++this._requestSeq
                const cacheKey = buildCacheKey({
                    searchArgs: this.searchArgs,
                    currentSort: this.currentSort,
                    currentSortDir: this.currentSortDir,
                    page: this.page,
                    perPageActive: this.perPageActive,
                })

                if (!force && this._cache.has(cacheKey)) {
                    const cached = this._cache.get(cacheKey)

                    // bump recency: Map iterates in insertion order, so
                    // delete+re-set moves this entry to the "most recent" end
                    this._cache.delete(cacheKey)
                    this._cache.set(cacheKey, cached)

                    this.rows = cached.rows
                    this.totalCount = cached.totalCount
                    this.loading = false
                    this.fetchError = null
                    return
                }

                this.loading = true
                this.fetchError = null

                const query = {
                    ...buildFilterParams(fieldTypes, this.searchArgs),
                    limit: `${ (this.page - 1) * this.perPageActive },${ this.perPageActive }`,
                }

                if (this.currentSort) {
                    query.order = `${ this.currentSort }:${ this.currentSortDir }`
                }

                try {
                    const { data } = await this.$_http({
                        method: 'GET',
                        url: `${ url }?${ param(query) }`,
                        withCredentials: true,
                    })

                    if (requestId !== this._requestSeq) return // a newer request already landed

                    const rows = Array.isArray(data.rows) ? data.rows : []

                    // Most endpoints don't include a total alongside paginated
                    // rows (QueryBuilder's ?count= replaces the row query
                    // rather than running alongside it) - fall back to a
                    // second, filters-only request instead of guessing. If
                    // that second request itself fails, don't let it blank
                    // out rows that already loaded fine - fall back to the
                    // old (known-imperfect) rows.length guess instead.
                    let totalCount = data.totalCount

                    if (!Number.isFinite(totalCount)) {
                        try {
                            totalCount = await this.fetchTotalCount()
                        } catch {
                            totalCount = rows.length
                        }
                    }

                    if (requestId !== this._requestSeq) return // stale after the second await too

                    this.rows = rows
                    this.totalCount = totalCount

                    if (maxCacheEntries > 0) {
                        this._cache.set(cacheKey, { rows, totalCount })

                        if (this._cache.size > maxCacheEntries) {
                            this._cache.delete(this._cache.keys().next().value)
                        }
                    }
                } catch (error) {
                    if (requestId !== this._requestSeq) return

                    this.fetchError = error
                    this.rows = []
                    this.totalCount = 0
                } finally {
                    if (requestId === this._requestSeq) {
                        this.loading = false
                    }
                }
            },
        },
        created() {
            this._requestSeq = 0
            this._cache = new Map()
            this._countCache = new Map()
            this._debouncedRemoteRefresh = debounce(() => this.refreshFromFirstPage(), searchDebounceMs)
        },
        beforeMount() {
            if (this.namespace && this.searchArgs) {
                loadFilters(this.namespace, this.searchArgs)
            }
        },
    }
}
